/**
 * Dashboard-specific formatting: big-number compaction, period labels, KPI
 * delta view-models (direction + goodness + text), opportunity band styling, the
 * review-backlog age string, and the confidence-band transform. Kept pure so the
 * numbers are unit-testable away from React.
 */
import type { KpiDelta } from '../../components';

/** Big-number formatting: `1,234` · `41k` · `1.8M` (design-reference §4.2). */
export function fmtBig(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${String(Math.round(n / 1000))}k`;
  return Math.round(n).toLocaleString('en-GB');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Parse a `YYYY-MM-DD` period as a UTC date, or null if it is not a plain day. */
function parseDay(period: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(period)) return null;
  const date = new Date(`${period}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Short axis label, `11 Jul`; a non-day period (week/month) passes through. */
export function periodLabel(period: string): string {
  const date = parseDay(period);
  if (date === null) return period;
  const month = MONTHS[date.getUTCMonth()] ?? '';
  return `${String(date.getUTCDate())} ${month}`;
}

/** Long tooltip label, `Sat 11 Jul`; a non-day period passes through. */
export function periodLongLabel(period: string): string {
  const date = parseDay(period);
  if (date === null) return period;
  const weekday = WEEKDAYS[date.getUTCDay()] ?? '';
  const month = MONTHS[date.getUTCMonth()] ?? '';
  return `${weekday} ${String(date.getUTCDate())} ${month}`;
}

/**
 * Period-over-period delta for a count metric (documents, pages): a percentage
 * change where up is always good. `new` when the previous window was empty.
 */
export function countDelta(current: number, previous: number): KpiDelta {
  if (previous <= 0) {
    return current > 0
      ? { value: 'new', direction: 'up', good: true }
      : { value: '0%', direction: 'up', good: true };
  }
  const pct = ((current - previous) / previous) * 100;
  const up = pct >= 0;
  return { value: `${Math.abs(pct).toFixed(1)}%`, direction: up ? 'up' : 'down', good: up };
}

export interface PointDeltaOptions {
  /** Whether an increase is the good direction for this metric. */
  goodWhenUp: boolean;
  /** Suffix, e.g. `pt` for percentage points, `''` for a raw confidence delta. */
  unit: string;
  /** Decimal places (default 1). */
  decimals?: number;
}

/**
 * Delta for a metric already expressed as a difference (percentage points or a
 * confidence delta). Colour follows goodness, not direction: a fall in
 * needs-review is good and draws green.
 */
export function pointDelta(delta: number, options: PointDeltaOptions): KpiDelta {
  const { goodWhenUp, unit, decimals = 1 } = options;
  const up = delta >= 0;
  return {
    value: `${Math.abs(delta).toFixed(decimals)}${unit}`,
    direction: up ? 'up' : 'down',
    good: goodWhenUp ? up : !up,
  };
}

export interface BandStyle {
  bg: string;
  fg: string;
  label: string;
}

/** Opportunity score → band colours + label (spec §8.1 score bands). */
export function bandStyle(score: number): BandStyle {
  if (score >= 80) return { bg: 'var(--ok-bg)', fg: 'var(--ok-ink)', label: 'High' };
  if (score >= 60) return { bg: 'var(--a-soft)', fg: 'var(--a-ink)', label: 'Medium' };
  if (score >= 40) return { bg: 'var(--warn-bg)', fg: 'var(--warn)', label: 'Low' };
  return { bg: 'var(--hov)', fg: 'var(--t2)', label: 'Minimal' };
}

/** Humanised age of the oldest review item, from its `created_at` to `now`. */
export function oldestAgeLabel(createdAt: string, now: number = Date.now()): string {
  const ms = now - new Date(createdAt).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'under a minute';
  const hours = Math.floor(minutes / 60);
  if (hours < 1) return `${String(minutes)} minute${minutes === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  if (days < 1) return `${String(hours)} hour${hours === 1 ? '' : 's'}`;
  return `${String(days)} day${days === 1 ? '' : 's'}`;
}

export interface ConfidenceBand {
  label: string;
  count: number;
  below: boolean;
}

interface ConfidenceRow {
  key: string;
  label: string;
  documents: number;
}

/**
 * Turn the real 4-band confidence breakdown into histogram bands, marking each
 * as below/above the acceptance threshold (default 0.75) from its upper edge, so
 * the chart colours below-threshold bands as needing review.
 */
export function confidenceBands(rows: readonly ConfidenceRow[], threshold = 0.75): ConfidenceBand[] {
  return rows.map((row) => {
    const parts = row.key.split('-');
    const upper = Number(parts[1]);
    const below = Number.isFinite(upper) ? upper <= threshold : false;
    return { label: row.label.replace('-', '–'), count: row.documents, below };
  });
}
